/**
 * FurtherTestingScreen v165.0.0 — Clean Card Layout
 * 
 * Streamlined orders flow replacing v120.4 chat-dialogue pattern.
 * Sections as stacked cards matching Orella design brief:
 *   1. Ordered Tests (amber/purple themed)
 *   2. Preparation Instructions (AI-generated via Haiku, collapsible)
 *   3. Where to Go (Google Places search + insurance cross-ref + Book Online)
 *   4. Upload Results (camera + file upload to Supabase Storage)
 *   5. Mark as Completed (sticky bottom)
 */

import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { MapPin, Phone, Check, FlaskConical, Camera, Upload, X, ChevronDown, Info, Clock, Shield, Search, Navigation, Calendar } from 'lucide-react'
import { useCaseStore, useProfileStore } from '../../stores'
import { useI18n } from '../../i18n'
import useActiveCarePlan from '../../hooks/useActiveCarePlan'
import { caseHasSignedVisit, signedOrdersPlaced } from '../../utils/signedPlan' // PROD-1079: no draft plan reaches the patient
import { uploadToStorage } from '../../services/storage'
import { deliverEntryIdentity } from '../../utils/deliverIdentity' // PROD-1042: identity sent in deliver._remove
import { CompactHeader } from '../../components/OrellaChatHeader'
import { asText, asFirst } from '../../utils/asText' // PROD-1156: a case-plan value is drawn as text, never as an object
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner

// ── Scan icon (not in lucide-react) ──
const Scan = ({ size = 24, className = '', style = {} }) => (
  <svg className={className} style={style} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 7V5a2 2 0 0 1 2-2h2"/><path d="M17 3h2a2 2 0 0 1 2 2v2"/><path d="M21 17v2a2 2 0 0 1-2 2h-2"/><path d="M7 21H5a2 2 0 0 1-2-2v-2"/><rect x="7" y="7" width="10" height="10" rx="1"/>
  </svg>
)

const NAVY = '#15354F'
const BLUE = '#46A8F9'
const LIGHT_BLUE = '#A7D9FC'
const ICE_BLUE = '#E9F8FF'
const GREEN = '#66D170'
const GREEN_DARK = '#16A34A'
const AMBER = '#F59E0B'

const TYPE_CONFIG_STRUCTURE = {
  radiology: { icon: Scan, titleKey: 'typeImaging', color: AMBER, lightBg: '#FEF3C7', borderColor: '#FDE68A' },
  imaging:   { icon: Scan, titleKey: 'typeImaging', color: AMBER, lightBg: '#FEF3C7', borderColor: '#FDE68A' },
  labs:      { icon: FlaskConical, titleKey: 'typeLabs', color: NAVY, lightBg: '#E9F8FF', borderColor: '#A7D9FC' },
}

// Known booking URLs for facility chains
const BOOKING_URLS = {
  'quest diagnostics': 'https://appointment.questdiagnostics.com/patient/confirmation',
  'labcorp': 'https://www.labcorp.com/labs-and-appointments',
  'radnet': 'https://www.radnet.com/patients/schedule-appointment',
  'simon med': 'https://www.simonmed.com/schedule-appointment',
  'akumin': 'https://www.akumin.com/schedule',
  'advanced radiology': 'https://www.advancedradiology.com/schedule',
  'touchstone imaging': 'https://www.touchstoneimaging.com/schedule',
}

function getBookingUrl(name) {
  if (!name) return null
  const lower = name.toLowerCase()
  for (const [chain, url] of Object.entries(BOOKING_URLS)) {
    if (lower.includes(chain)) return url
  }
  return null
}

// Section label
const SectionLabel = ({ text }) => (
  <div className="px-4 mb-1.5">
    <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">{text}</span>
  </div>
)

export default function FurtherTestingScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const { currentCase, updateDeliverTask } = useCaseStore()
  const { profile } = useProfileStore()
  const { t } = useI18n()
  const fileInputRef = useRef(null)
  const cameraInputRef = useRef(null)

  const TYPE_CONFIG = useMemo(() => {
    const result = {}
    Object.entries(TYPE_CONFIG_STRUCTURE).forEach(([k, v]) => {
      result[k] = { ...v, title: t('furtherTesting.' + v.titleKey) }
    })
    return result
  }, [t])

  const testType = location.state?.testType || 'radiology'
  const config = TYPE_CONFIG[testType] || TYPE_CONFIG.radiology
  const Icon = config.icon
  const isImaging = testType === 'radiology' || testType === 'imaging'
  const taskKey = isImaging ? 'imaging' : testType

  // ── State ──
  const [showPrep, setShowPrep] = useState(true)
  const [prepData, setPrepData] = useState(null)
  const [prepLoading, setPrepLoading] = useState(false)
  // v237.2.0: test education (order reason + Learn-more primer)
  const [testEdu, setTestEdu] = useState(null)
  const [eduLoading, setEduLoading] = useState(false)
  const [openAbout, setOpenAbout] = useState(new Set())
  const [selectedFacility, setSelectedFacility] = useState(null)
  const [facilitySearch, setFacilitySearch] = useState('')
  const [facilityResults, setFacilityResults] = useState([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [showAuth, setShowAuth] = useState(false)
  const [authNumber, setAuthNumber] = useState('')
  const [uploadedFiles, setUploadedFiles] = useState([])
  const [uploading, setUploading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isMdRecommended, setIsMdRecommended] = useState(false)
  // Appointment details (manual entry until booking API is wired)
  const [apptDate, setApptDate] = useState('')
  const [apptTime, setApptTime] = useState('')
  const [apptConfirmation, setApptConfirmation] = useState('')
  const [showApptForm, setShowApptForm] = useState(false)
  const [savingAppt, setSavingAppt] = useState(false)

  const caseInfo = `${(currentCase?.specialtyName || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} > ${currentCase?.focusAreaName || ''}`

  // ── Care plan data ──
  const deliverData = useActiveCarePlan(currentCase)
  // ★ PROD-1079: no signed visit -> back to Case Home (DeliverScreen's backstop, same rule).
  //   Before signing, cases.follow_up is md's draft plan (utils/signedPlan.js).
  useEffect(() => {
    if (currentCase && !caseHasSignedVisit(currentCase)) navigate('/case/home', { replace: true })
  }, [currentCase, navigate])

  const testData = useMemo(() => {
    if (!currentCase || !deliverData) return { tests: [] }
    let tests = isImaging
      ? (deliverData.imaging?.length > 0 ? deliverData.imaging : (location.state?.items || []))
      : (deliverData.labs?.length > 0 ? deliverData.labs : (location.state?.items || []))
    // Fallback into diagnose/ordersPlaced. PROD-1079: only once the case has a signed visit (before
    //   that both are md's draft; the route guard above redirects anyway).
    if (tests.length === 0) {
      const diag = caseHasSignedVisit(currentCase) ? (currentCase.diagnose || {}) : {}
      const op = signedOrdersPlaced(currentCase)
      tests = isImaging ? (op.imaging || op.radiology || diag.selectedImaging || []) : (op.labs || diag.selectedLabs || [])
    }
    return { tests: Array.isArray(tests) ? tests : [] }
  }, [currentCase, deliverData, testType, location.state?.items])

  const dueInfo = useMemo(() => {
    const dd = deliverData?.dueDates?.[taskKey]
    if (!dd || dd === 'by-followup') return deliverData?.followUpTiming ? t('deliver.dueDate', { date: deliverData.followUpTiming }) : null
    if (dd === 'asap') return 'ASAP'
    return null
  }, [deliverData, taskKey])

  // ── Insurance ──
  const insuranceInfo = useMemo(() => {
    const ins = profile?.insurance || {}
    if (ins.selfPay) return null
    const name = ins.provider || ins.insurerName || ''
    return name ? { provider: name, type: (ins.coverageType || ins.planType || '').toUpperCase() } : null
  }, [profile])

  // ── Restore saved state ──
  useEffect(() => {
    const saved = isImaging ? deliverData?.imagingFacility : deliverData?.labsFacility
    if (saved && !selectedFacility) {
      setSelectedFacility(saved)
      // Check if this came from ordersPlaced (MD-set) vs deliver (patient-set)
      const ordersPlaced = signedOrdersPlaced(currentCase) // PROD-1079
      const mdFacility = isImaging ? ordersPlaced.imagingFacility : ordersPlaced.labsFacility
      if (mdFacility?.name && mdFacility.name === saved.name) setIsMdRecommended(true)
    }
    const savedAuth = isImaging ? deliverData?.imagingAuth : deliverData?.labsAuth
    if (savedAuth) setAuthNumber(savedAuth)
    // Restore saved appointment details
    const savedAppt = currentCase?.deliver?.[isImaging ? 'imagingAppointment' : 'labsAppointment']
    if (savedAppt) {
      if (savedAppt.date) setApptDate(savedAppt.date)
      if (savedAppt.time) setApptTime(savedAppt.time)
      if (savedAppt.confirmation) setApptConfirmation(savedAppt.confirmation)
    }
  }, [deliverData, isImaging])

  useEffect(() => {
    // ★ CANONICAL FIRST, LEGACY SECOND. `radiologyFiles` is the key every consumer
    //   reads; `imagingFiles` survives only on rows written before this change.
    const key = isImaging ? 'radiologyFiles' : 'labsFiles'
    const existing = currentCase?.deliver?.[key] || currentCase?.deliver?.imagingFiles || []
    if (existing.length > 0 && uploadedFiles.length === 0) setUploadedFiles(existing.map(f => ({ ...f, existing: true })))
  }, [currentCase?.id])

  // ── AI Prep ──
  useEffect(() => {
    if (testData.tests.length === 0) return
    const cached = isImaging ? deliverData?.imagingPrep : deliverData?.labsPrep
    if (cached?.steps) { setPrepData(cached); return }
    const go = async () => {
      setPrepLoading(true)
      try {
        const { aiAPI } = await import('../../services/api')
        const res = await aiAPI.generateOrderPrep(currentCase?.id, testType, testData.tests)
        if (res?.success && res.prep) {
          setPrepData(res.prep)
          const key = isImaging ? 'imagingPrep' : 'labsPrep'
          updateDeliverTask(taskKey, { [key]: res.prep })
        }
      } catch (err) { console.warn('[FurtherTesting] AI prep error:', err) }
      finally { setPrepLoading(false) }
    }
    go()
  }, [testData.tests.length])

  // ── v237.2.0: Test education — reason + Learn-more primer (Haiku, cached) ──
  useEffect(() => {
    if (testData.tests.length === 0) return
    const cached = isImaging ? deliverData?.imagingTestEducation : deliverData?.labsTestEducation
    if (cached?.education?.length >= testData.tests.length) { setTestEdu(cached); return }
    const go = async () => {
      setEduLoading(true)
      try {
        const { aiAPI } = await import('../../services/api')
        const res = await aiAPI.generateTestEducation(currentCase?.id, testType, testData.tests)
        if (res?.success && res.education?.education?.length) {
          setTestEdu(res.education)
          const key = isImaging ? 'imagingTestEducation' : 'labsTestEducation'
          updateDeliverTask(taskKey, { [key]: res.education })
        }
      } catch (err) { console.warn('[FurtherTesting] test edu error:', err) }
      finally { setEduLoading(false) }
    }
    go()
  }, [testData.tests.length])

  // v238.1.0: fallbackPrep was clobbered by the v237.2.0 test-education useEffect insertion,
  // leaving a dangling ternary + undefined `fallbackPrep` that crashed the screen on render. Restored.
  const fallbackPrep = testType === 'labs'
    ? [{ text: t('furtherTesting.fast812HoursBeforeBlood'), important: true }, { text: t('furtherTesting.stayHydrated'), important: false }, { text: t('furtherTesting.bringInsuranceCardAndPhoto'), important: false }, { text: t('furtherTesting.arrive10MinutesEarly'), important: false }]
    : [{ text: t('furtherTesting.doNotEatOrDrink'), important: true }, { text: t('furtherTesting.removeJewelryPiercingsMetalObjects'), important: false }, { text: t('furtherTesting.bringInsuranceCardAndPhoto'), important: false }, { text: t('furtherTesting.arrive15MinutesEarly'), important: false }]
  const prepSteps = prepData?.steps || fallbackPrep

  // ── Google Places search ──
  const searchFacilities = useCallback((query) => {
    if (!query || query.length < 3) { setFacilityResults([]); return }
    setSearchLoading(true)
    if (window.google?.maps?.places) {
      const svc = new window.google.maps.places.PlacesService(document.createElement('div'))
      const typeHint = testType === 'labs' ? 'laboratory' : 'medical imaging'
      svc.textSearch({ query: `${query} ${typeHint}`, type: 'health' }, (results, status) => {
        if (status === 'OK' && results) {
          setFacilityResults(results.slice(0, 5).map(p => ({
            placeId: p.place_id, name: p.name, address: p.formatted_address,
            rating: p.rating, reviews: p.user_ratings_total, phone: '',
            insMatch: ['quest diagnostics', 'labcorp', 'radnet'].some(c => (p.name || '').toLowerCase().includes(c)),
            bookingUrl: getBookingUrl(p.name),
          })))
        }
        setSearchLoading(false)
      })
    } else { setSearchLoading(false) }
  }, [testType])

  useEffect(() => {
    const t = setTimeout(() => { if (facilitySearch.length >= 3) searchFacilities(facilitySearch) }, 400)
    return () => clearTimeout(t)
  }, [facilitySearch, searchFacilities])

  // ── Handlers ──
  const handleSelectFacility = async (f) => {
    setSelectedFacility(f); setFacilitySearch(''); setFacilityResults([])
    const fKey = isImaging ? 'imagingFacility' : 'labsFacility'
    const sKey = isImaging ? 'imagingStatus' : 'labsStatus'
    await updateDeliverTask(taskKey, { [fKey]: f, [sKey]: 'scheduled' })
  }

  const [savingAuth, setSavingAuth] = useState(false)

  const handleSaveAuth = async () => {
    if (!authNumber.trim() || savingAuth) return
    setSavingAuth(true)
    try {
      const key = isImaging ? 'imagingAuth' : 'labsAuth'
      await updateDeliverTask(taskKey, { [key]: authNumber.trim() })
    } catch (err) { console.error('[FurtherTesting] Auth save failed:', err) }
    finally { setSavingAuth(false) }
  }

  const handleSaveAppointment = async () => {
    if (!apptDate || savingAppt) return
    setSavingAppt(true)
    try {
      const apptKey = isImaging ? 'imagingAppointment' : 'labsAppointment'
      const statusKey = isImaging ? 'imagingStatus' : 'labsStatus'
      await updateDeliverTask(taskKey, {
        [apptKey]: { date: apptDate, time: apptTime, confirmation: apptConfirmation, facilityName: selectedFacility?.name || '' },
        [statusKey]: 'scheduled',
      })
      setShowApptForm(false)
    } catch (err) {
      console.error('[FurtherTesting] Save appointment failed:', err)
    } finally {
      setSavingAppt(false)
    }
  }

  const formatApptDate = (d) => {
    if (!d) return ''
    try {
      const [y, m, day] = d.split('-')
      // PROD-1329: a date-only value is a calendar day, not an instant (same defect as PROD-1278).
      // Build it at UTC noon and format it in UTC so every device time zone shows the same day.
      return new Date(Date.UTC(parseInt(y), parseInt(m) - 1, parseInt(day), 12)).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' })
    } catch { return d }
  }

  const formatApptTime = (t) => {
    if (!t) return ''
    try {
      const [h, m] = t.split(':')
      const hr = parseInt(h)
      return `${hr === 0 ? 12 : hr > 12 ? hr - 12 : hr}:${m} ${hr >= 12 ? 'PM' : 'AM'}`
    } catch { return t }
  }

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || e.dataTransfer?.files || [])
    if (!files.length) return
    setUploading(true)
    const added = []
    for (const file of files) {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`
      try {
        const url = await uploadToStorage(currentCase?.id, file.name, file, file.type)
        added.push({ id, name: file.name, size: file.size, type: file.type, url, uploadedAt: new Date().toISOString() })
      } catch { added.push({ id, name: file.name, size: file.size, type: file.type }) }
    }
    const allFiles = [...uploadedFiles, ...added]
    setUploadedFiles(allFiles)
    // Immediately persist to deliver data so MD panel can see uploads
    // ★ DELIVER-IMAGING-DUPKEY-1 — one array, passed once. The store decides the
    //   column name; this screen no longer names it twice. For labs both names were
    //   already 'labsFiles', so the object literal silently collapsed to one key and
    //   only imaging ever duplicated — which is why it went unnoticed.
    // ★ PROD-1103: only the NEW entries go out; the API merges them per entry into what is stored.
    const fileRefs = added.map(f => ({ id: f.id, name: f.name, size: f.size, url: f.url, uploadedAt: f.uploadedAt }))
    await updateDeliverTask(taskKey, { files: fileRefs })
    setUploading(false)
    if (e.target) e.target.value = ''
  }

  // Drag & drop handlers
  const [isDragging, setIsDragging] = useState(false)
  const dropRef = useRef(null)

  const handleDragOver = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true) }, [])
  const handleDragLeave = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false) }, [])
  const handleDrop = useCallback((e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false)
    if (e.dataTransfer?.files?.length > 0) handleFileSelect(e)
  }, [uploadedFiles, currentCase?.id])

  const handleSave = async () => {
    if (isSubmitting) return
    setIsSubmitting(true)
    try {
      // ★ PROD-1103: no `files` key: uploads were already persisted by handleFileSelect, and a list sent
      //   here (empty, or only today's) must never stand for the whole list.
      const updates = {}
      if (selectedFacility) {
        updates[isImaging ? 'imagingFacility' : 'labsFacility'] = selectedFacility
        updates[isImaging ? 'imagingStatus' : 'labsStatus'] = 'scheduled'
      }
      if (authNumber.trim()) {
        updates[isImaging ? 'imagingAuth' : 'labsAuth'] = authNumber.trim()
      }
      if (apptDate) {
        updates[isImaging ? 'imagingAppointment' : 'labsAppointment'] = { date: apptDate, time: apptTime, confirmation: apptConfirmation, facilityName: selectedFacility?.name || '' }
      }
      await updateDeliverTask(taskKey, updates)
      navigate('/case/deliver')
    } catch (err) { console.error('[FurtherTesting] Save failed:', err) }
    finally { setIsSubmitting(false) }
  }

  const handleMarkComplete = async () => {
    if (isSubmitting) return
    setIsSubmitting(true)
    try {
      await updateDeliverTask(taskKey, {
        completed: true,
        [`${taskKey}CompletedAt`]: new Date().toISOString(),
        [`${taskKey}Status`]: 'completed',
      })
      navigate('/case/deliver')
    } catch (err) { console.error('[FurtherTesting] Complete failed:', err) }
    finally { setIsSubmitting(false) }
  }

  const txt = (item) => typeof item === 'string' ? item : asFirst(item?.name, item?.test, item?.description) || 'Test' // PROD-1156
  const cptCode = (item) => item && typeof item === 'object' ? asFirst(item.code, item.cptCode) : '' // PROD-1156 LOW-5: order detail is drawn as text
  const ts = (key, fb) => { const v = t(key); return (!v || v === key) ? fb : v }
  const fld = (item, keys) => { if (typeof item !== 'object' || !item) return ''; for (const k of keys) { const v = item[k]; if (v) { const s = typeof v === 'boolean' ? v : asText(v); if (s) return s } } return '' } // PROD-1156 LOW-5: an object detail never reaches JSX

  return (
    <div className="min-h-full flex flex-col bg-[#F8FAFC]">
      <CompactHeader stepNum={4} totalSteps={4} stepLabel="Deliver" caseInfo={caseInfo} statusLabel="Active" onBack={() => navigate('/case/deliver')} />

      <div className="flex-1 overflow-auto pt-3 pb-6">

        {/* ━━ 1. YOUR DOCTOR'S ORDER ━━ v237.2.0 */}
        <SectionLabel text={ts('furtherTesting.yourDoctorsOrder', "Your doctor's order")} />
        <div className="mx-4 mb-4 rounded-[14px] border-[1.5px] bg-white overflow-hidden" style={{ borderColor: config.borderColor, borderTopWidth: 3, borderTopColor: config.color }}>
          <div className="p-3.5">
            {testData.tests.length > 0 ? testData.tests.map((test, i) => {
              const edu = testEdu?.education?.[i]
              const reason = edu?.reason
              const hasAbout = !!(edu && (edu.whatItIs || edu.whatHappens))
              const isOpen = openAbout.has(i)
              const side = fld(test, ['laterality', 'side'])
              const region = fld(test, ['bodyPart', 'region'])
              const contrast = fld(test, ['contrast'])
              const specimen = fld(test, ['specimen'])
              const fasting = fld(test, ['fasting'])
              const priority = (fld(test, ['priority']) || '').toString()
              const isStat = /stat|urgent/i.test(priority)
              const fields = isImaging
                ? [['Side', side && (side + (/(left|right)/i.test(side) ? ' ear' : ''))], ['Region', region], ['Contrast', contrast]]
                : [['Specimen', specimen], ['Fasting', fasting]]
              const shown = fields.filter(([, v]) => v)
              return (
                <div key={i} className={i > 0 ? 'mt-3 pt-3 border-t border-gray-100' : ''}>
                  <div className="flex items-start gap-2.5">
                    <div className="w-8 h-8 rounded-[9px] flex items-center justify-center flex-shrink-0" style={{ background: config.lightBg }}>
                      <Icon size={16} style={{ color: config.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13.5px] font-bold leading-snug" style={{ color: NAVY }}>{txt(test)}</p>
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        <span className="text-[9px] font-extrabold uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ background: '#EEF2F6', color: '#475569' }}>{config.title}</span>
                        {isStat && <span className="text-[9px] font-extrabold uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ background: '#FEF3C7', color: '#92560C' }}>STAT</span>}
                        {cptCode(test) && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ background: '#EEF2F6', color: '#475569' }}>CPT {cptCode(test)}</span>}
                      </div>
                    </div>
                  </div>

                  {(shown.length > 0 || reason) && (
                    <div className="grid grid-cols-2 gap-1.5 mt-2.5">
                      {shown.map(([k, v]) => (
                        <div key={k} className="rounded-[10px] px-2.5 py-1.5" style={{ background: '#F8FAFC', border: '1px solid #EDF1F5' }}>
                          <p className="text-[9px] font-extrabold uppercase tracking-wide" style={{ color: '#94A3B8' }}>{k}</p>
                          <p className="text-[12px] font-semibold" style={{ color: NAVY }}>{v}</p>
                        </div>
                      ))}
                      {reason && (
                        <div className="col-span-2 rounded-[10px] px-2.5 py-2" style={{ background: '#F8FAFC', border: '1px solid #EDF1F5' }}>
                          <p className="text-[9px] font-extrabold uppercase tracking-wide" style={{ color: '#94A3B8' }}>{ts('furtherTesting.reasonForTest', 'Reason for this test')}</p>
                          <p className="text-[12px] font-medium leading-snug mt-0.5" style={{ color: '#475569' }}>{reason}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {typeof test === 'object' && test.notes && <p className="text-[10.5px] text-gray-400 mt-1.5">{test.notes}</p>}

                  {hasAbout && (
                    <>
                      <button onClick={() => setOpenAbout(prev => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n })}
                        className="inline-flex items-center gap-1.5 mt-2.5 bg-transparent p-0" style={{ color: BLUE, fontSize: 12.5, fontWeight: 700, border: 'none' }}>
                        {ts('furtherTesting.learnMoreTest', 'Learn more about this test')}
                        <ChevronDown size={14} strokeWidth={2.5} style={{ transition: 'transform .2s', transform: isOpen ? 'rotate(180deg)' : 'none' }} />
                      </button>
                      <div className="overflow-hidden" style={{ transition: 'max-height .3s ease', maxHeight: isOpen ? 360 : 0 }}>
                        <div className="mt-2 rounded-[11px] px-3 py-2.5" style={{ background: '#F6FAFD', border: '1px solid #E4EFF7' }}>
                          {edu.whatItIs && (<>
                            <p className="text-[9.5px] font-extrabold uppercase tracking-wide mb-0.5" style={{ color: BLUE }}>{ts('furtherTesting.whatThisTestIs', 'What this test is')}</p>
                            <p className="text-[12px] leading-relaxed font-medium mb-2" style={{ color: '#475569' }}>{edu.whatItIs}</p>
                          </>)}
                          {edu.whatHappens && (<>
                            <p className="text-[9.5px] font-extrabold uppercase tracking-wide mb-0.5" style={{ color: BLUE }}>{ts('furtherTesting.whatHappens', 'What happens')}</p>
                            <p className="text-[12px] leading-relaxed font-medium" style={{ color: '#475569' }}>{edu.whatHappens}</p>
                          </>)}
                        </div>
                      </div>
                    </>
                  )}

                  {/* v238.1.0: per-test loading spinner so the item isn't blank while Haiku generates the Learn-more details */}
                  {!hasAbout && eduLoading && !testEdu && (
                    <div className="inline-flex items-center gap-1.5 mt-2.5">
                      <OrellaSpinner size={16} />
                      <span className="text-[12px] font-medium" style={{ color: '#94A3B8' }}>{ts('furtherTesting.preparingDetails', 'Preparing details…')}</span>
                    </div>
                  )}
                </div>
              )
            }) : <p className="text-[12px] text-gray-400 py-2">{t('furtherTesting.noSpecificTestsListed')}</p>}

            <div className="flex items-start gap-2 mt-3 pt-3 border-t border-gray-100">
              <Info size={12} className="flex-shrink-0 mt-0.5" style={{ color: '#94A3B8' }} />
              <span className="text-[10.5px] font-medium leading-snug" style={{ color: '#64748B' }}>{ts('furtherTesting.eorderNote', 'Your clinic can send this order to the testing center electronically — or bring this summary with you.')}</span>
            </div>

            {dueInfo && (
              <div className="flex items-center gap-1.5 mt-2.5 pt-2.5 border-t border-gray-100">
                <Clock size={11} className="text-gray-400" />
                <span className="text-[11px] text-gray-500 font-medium">{dueInfo}</span>
              </div>
            )}
          </div>
        </div>

        {/* ━━ 2. PREPARATION ━━ */}
        <SectionLabel text={t('furtherTesting.howToPrepare')} />
        <div className="mx-4 mb-4 rounded-[14px] border-[1.5px] bg-white overflow-hidden" style={{ borderColor: LIGHT_BLUE, borderTopWidth: 3, borderTopColor: BLUE }}>
          <button onClick={() => setShowPrep(!showPrep)} className="w-full flex items-center justify-between p-3.5">
            <div className="flex items-center gap-2">
              <Info size={15} style={{ color: BLUE }} />
              <span className="text-[13px] font-bold" style={{ color: NAVY }}>{t('furtherTesting.preparationInstructions')}</span>
              {prepLoading && <OrellaSpinner size={16} />}
            </div>
            <ChevronDown size={14} className={`text-gray-400 transition-transform duration-200 ${showPrep ? 'rotate-180' : ''}`} />
          </button>
          {showPrep && (
            <div className="px-3.5 pb-3.5">
              {prepData?.warnings?.length > 0 && (
                <div className="mb-3 p-2.5 rounded-[10px] bg-red-50 border border-red-200">
                  {prepData.warnings.map((w, i) => <p key={i} className="text-[11.5px] text-red-700 font-medium leading-snug">{w}</p>)}
                </div>
              )}
              {prepSteps.map((step, i) => {
                const s = typeof step === 'string' ? { text: step } : step
                return (
                  <div key={i} className="flex items-start gap-2.5 mb-2">
                    <div className="w-5 h-5 rounded-full bg-[#46A8F9] text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">{i + 1}</div>
                    <span className="text-[12.5px] leading-snug text-gray-700">{s.text}</span>
                  </div>
                )
              })}
              {prepData?.note && (
                <div className="flex items-center gap-1.5 mt-1.5 py-1.5 px-2.5 rounded-lg" style={{ background: ICE_BLUE }}>
                  <Info size={10} style={{ color: BLUE }} />
                  <span className="text-[10px] font-medium" style={{ color: BLUE }}>{prepData.note}</span>
                </div>
              )}
              {/* MD prep notes */}
              {asText(isImaging ? deliverData?.imagingPrepNotes : deliverData?.labsPrepNotes) && (
                <div className="mt-2 p-2.5 rounded-[10px] border border-[#A7D9FC] bg-[#E9F8FF]">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#15354F] mb-1">{t('furtherTesting.noteFromYourDoctor')}</p>
                  <p className="text-[12px] text-[#15354F] leading-snug">{asText(isImaging ? deliverData.imagingPrepNotes : deliverData.labsPrepNotes)}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ━━ 3. WHERE TO GO ━━ */}
        <SectionLabel text={t('furtherTesting.whereToGo')} />
        <div className="mx-4 mb-4 rounded-[14px] border-[1.5px] border-green-200 bg-white overflow-hidden" style={{ borderTopWidth: 3, borderTopColor: GREEN }}>
          <div className="p-3.5">
            {!selectedFacility ? (
              <>
                <div className="flex items-center gap-2 p-2.5 rounded-[10px] border-[1.5px] border-gray-200 bg-[#F8FAFC] mb-2">
                  <Search size={14} className="text-gray-400" />
                  <input value={facilitySearch} onChange={e => setFacilitySearch(e.target.value)}
                    placeholder={t('furtherTesting.searchFacilityPlaceholder')} className="flex-1 bg-transparent outline-none text-[12.5px] text-[#15354F] placeholder-gray-400" />
                  {facilitySearch && <button onClick={() => { setFacilitySearch(''); setFacilityResults([]) }}><X size={12} className="text-gray-400" /></button>}
                  {searchLoading && <OrellaSpinner size={16} tone="inherit" />}
                </div>
                {insuranceInfo && (
                  <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full mb-2.5" style={{ background: GREEN + '12' }}>
                    <Shield size={10} style={{ color: GREEN_DARK }} />
                    <span className="text-[9.5px] font-semibold" style={{ color: GREEN_DARK }}>{insuranceInfo.provider} {insuranceInfo.type}</span>
                  </div>
                )}
                {facilityResults.map(f => (
                  <button key={f.placeId} onClick={() => handleSelectFacility(f)} className="w-full text-left p-3 rounded-[12px] border-[1.5px] border-gray-200 bg-white mb-2 transition-colors hover:border-[#46A8F9]">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <span className="text-[13px] font-semibold" style={{ color: NAVY }}>{f.name}</span>
                      {f.insMatch && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold shrink-0" style={{ background: GREEN + '12', color: GREEN_DARK }}><Shield size={9} /> {t('furtherTesting.innetwork')}</span>}
                    </div>
                    <p className="text-[11px] text-gray-500 mb-1.5">{f.address}</p>
                    <div className="flex items-center justify-between">
                      {f.rating && <span className="text-[10.5px] text-gray-400">⭐ {f.rating}{f.reviews ? ` (${f.reviews})` : ''}</span>}
                      {f.bookingUrl && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10.5px] font-bold text-white" style={{ background: GREEN }}><Calendar size={10} /> {t('furtherTesting.bookOnline', 'Book Online')}</span>}
                    </div>
                  </button>
                ))}
                {facilitySearch.length >= 3 && !searchLoading && facilityResults.length === 0 && (
                  <p className="text-[11px] text-gray-400 text-center py-4">{t('furtherTesting.noResultsTryADifferent')}</p>
                )}
                {facilitySearch.length < 3 && facilityResults.length === 0 && (
                  <div className="text-center py-6">
                    <MapPin size={20} className="text-gray-300 mx-auto mb-2" />
                    <p className="text-[11.5px] text-gray-400">{t('furtherTesting.searchNearYou', { kind: testType === 'labs' ? t('furtherTesting.kindLab', 'lab') : t('furtherTesting.kindImaging', 'imaging center'), defaultValue: 'Search for a {{kind}} near you' })}</p>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="p-3 rounded-[12px] bg-[#F8FAFC] border-[1.5px] border-gray-200">
                  <div className="flex justify-between items-start mb-1">
                    <span className="text-[14px] font-bold" style={{ color: NAVY }}>{selectedFacility.name}</span>
                    <button onClick={() => { setSelectedFacility(null); setIsMdRecommended(false) }} className="text-[11px] font-semibold" style={{ color: BLUE }}>{t('furtherTesting.change')}</button>
                  </div>
                  {isMdRecommended && (
                    <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full mb-1.5" style={{ background: BLUE + '12' }}>
                      <span className="text-[9px] font-semibold" style={{ color: BLUE }}>👨‍⚕️ {t('furtherTesting.mdRecommended')}</span>
                    </div>
                  )}
                  {selectedFacility.insMatch && (
                    <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full mb-2" style={{ background: GREEN + '12' }}>
                      <Shield size={9} style={{ color: GREEN_DARK }} /><span className="text-[9px] font-semibold" style={{ color: GREEN_DARK }}>{t('furtherTesting.innetwork')}</span>
                    </div>
                  )}
                  {selectedFacility.address && <div className="flex items-center gap-1.5 text-[11.5px] text-gray-500 mb-1"><MapPin size={11} className="text-gray-400" /> {selectedFacility.address}</div>}
                  {selectedFacility.phone && <div className="flex items-center gap-1.5 text-[11.5px] mb-1"><Phone size={11} className="text-gray-400" /><a href={`tel:${selectedFacility.phone}`} className="font-semibold" style={{ color: BLUE }}>{selectedFacility.phone}</a></div>}
                  <div className="mt-3 space-y-2">
                    {selectedFacility.bookingUrl && (
                      <a href={selectedFacility.bookingUrl} target="_blank" rel="noopener noreferrer"
                        className="w-full py-2.5 rounded-[10px] text-[13px] font-bold text-white flex items-center justify-center gap-1.5 no-underline" style={{ background: GREEN, boxShadow: `0 2px 8px ${GREEN}40` }}>
                        <Calendar size={14} /> {t('furtherTesting.bookAppointmentOnline', 'Book Appointment Online')}
                      </a>
                    )}
                    <div className="flex gap-2">
                      <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedFacility.address || selectedFacility.name)}`} target="_blank" rel="noopener noreferrer"
                        className="flex-1 py-2.5 rounded-[10px] text-[12px] font-semibold text-white flex items-center justify-center gap-1.5 no-underline" style={{ background: BLUE }}>
                        <Navigation size={12} /> {t('furtherTesting.directions', 'Directions')}
                      </a>
                      {selectedFacility.phone && (
                        <a href={`tel:${selectedFacility.phone}`} className="py-2.5 px-4 rounded-[10px] border-[1.5px] border-gray-200 text-gray-600 flex items-center justify-center no-underline"><Phone size={14} /></a>
                      )}
                    </div>
                  </div>
                </div>

                {/* Appointment details */}
                {apptDate && !showApptForm ? (
                  /* ── Saved appointment — compact card (matches AppointmentEntry pattern) ── */
                  <div className="mt-2 rounded-[12px] border-[1.5px] border-blue-200 bg-white overflow-hidden">
                    <div className="flex items-center gap-2 px-3 py-2 border-l-[3px] border-l-[#46A8F9]" style={{ background: ICE_BLUE }}>
                      <Calendar size={13} style={{ color: BLUE }} />
                      <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: NAVY }}>{t('furtherTesting.yourAppointment')}</span>
                      <button onClick={() => setShowApptForm(true)} className="ml-auto text-[10px] font-semibold" style={{ color: BLUE }}>{t('furtherTesting.edit')}</button>
                    </div>
                    <div className="px-3 py-2.5 space-y-1">
                      <div className="flex items-center gap-2">
                        <Calendar size={12} className="text-gray-400" />
                        <span className="text-[13px] font-bold" style={{ color: NAVY }}>{formatApptDate(apptDate)}</span>
                      </div>
                      {apptTime && (
                        <div className="flex items-center gap-2">
                          <Clock size={12} className="text-gray-400" />
                          <span className="text-[12px] text-gray-600">{formatApptTime(apptTime)}</span>
                        </div>
                      )}
                      {apptConfirmation && (
                        <div className="flex items-center gap-2">
                          <Check size={12} className="text-gray-400" />
                          <span className="text-[12px] text-gray-500">{t('furtherTesting.confirmationLabel', 'Confirmation:')} <strong className="text-gray-700">{apptConfirmation}</strong></span>
                        </div>
                      )}
                    </div>
                  </div>
                ) : showApptForm || !apptDate ? (
                  /* ── Appointment form ── */
                  <div className="mt-2">
                    {!showApptForm && !apptDate ? (
                      <button onClick={() => setShowApptForm(true)}
                        className="w-full flex items-center gap-2 p-2.5 rounded-[10px] border-[1.5px] border-dashed border-blue-200" style={{ background: ICE_BLUE + '40' }}>
                        <Calendar size={13} style={{ color: BLUE }} />
                        <span className="text-[11.5px] font-medium flex-1 text-left" style={{ color: BLUE }}>{t('furtherTesting.addAppointmentDetails')}</span>
                        <ChevronDown size={12} style={{ color: BLUE }} />
                      </button>
                    ) : (
                      <div className="rounded-[12px] border-[1.5px] border-blue-200 bg-white overflow-hidden">
                        <div className="flex items-center gap-2 px-3 py-2 border-l-[3px] border-l-[#46A8F9]" style={{ background: ICE_BLUE }}>
                          <Calendar size={13} style={{ color: BLUE }} />
                          <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: NAVY }}>{t('furtherTesting.appointmentDetails')}</span>
                        </div>
                        <div className="p-3 space-y-2.5">
                          <div>
                            <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block mb-1">{t('furtherTesting.date')}</label>
                            <input type="date" value={apptDate} onChange={e => setApptDate(e.target.value)}
                              min={new Date().toISOString().split('T')[0]}
                              className="w-full px-3 py-2 text-[13px] border-[1.5px] border-gray-200 rounded-[10px] outline-none focus:border-[#46A8F9]" style={{ color: NAVY }} />
                          </div>
                          <div>
                            <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block mb-1">{t('furtherTesting.time')}</label>
                            <input type="time" value={apptTime} onChange={e => setApptTime(e.target.value)}
                              className="w-full px-3 py-2 text-[13px] border-[1.5px] border-gray-200 rounded-[10px] outline-none focus:border-[#46A8F9]" style={{ color: NAVY }} />
                          </div>
                          <div>
                            <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block mb-1">{t('furtherTesting.confirmationOptional')}</label>
                            <input type="text" value={apptConfirmation} onChange={e => setApptConfirmation(e.target.value)}
                              placeholder="e.g. PUPEJT"
                              className="w-full px-3 py-2 text-[13px] border-[1.5px] border-gray-200 rounded-[10px] outline-none focus:border-[#46A8F9] placeholder-gray-300" style={{ color: NAVY }} />
                          </div>
                          <div className="flex gap-2 pt-1">
                            <button onClick={() => { setShowApptForm(false); if (!apptDate) { setApptTime(''); setApptConfirmation('') } }}
                              disabled={savingAppt}
                              className="px-4 py-2 rounded-[10px] border-[1.5px] border-gray-200 text-[12px] font-semibold text-gray-400">{t('furtherTesting.cancel')}</button>
                            <button onClick={handleSaveAppointment} disabled={!apptDate || savingAppt}
                              className={`flex-1 py-2 rounded-[10px] text-[12px] font-bold border-none flex items-center justify-center gap-1.5 ${apptDate && !savingAppt ? 'text-white cursor-pointer' : 'bg-gray-200 text-gray-400'}`}
                              style={apptDate && !savingAppt ? { background: BLUE } : undefined}>
                              {savingAppt && <OrellaSpinner size={16} tone="inherit" />}
                              {savingAppt ? 'Saving...' : 'Save Appointment'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}

                <button onClick={() => setShowAuth(!showAuth)} className="w-full flex items-center gap-2 mt-2 p-2.5 rounded-[10px] border-[1.5px] border-dashed border-gray-300">
                  <Shield size={12} className={showAuth ? 'text-[#46A8F9]' : 'text-gray-400'} />
                  <span className={`text-[11px] font-medium flex-1 text-left ${showAuth ? 'text-[#15354F]' : 'text-gray-400'}`}>{showAuth ? 'Prior Authorization' : 'Add authorization number'}</span>
                  <ChevronDown size={12} className={`text-gray-400 transition-transform ${showAuth ? 'rotate-180' : ''}`} />
                </button>
                {showAuth && (
                  <div className="flex gap-2 mt-1.5">
                    <input value={authNumber} onChange={e => setAuthNumber(e.target.value)} placeholder="Auth #"
                      className="flex-1 py-2 px-3 rounded-lg border-[1.5px] border-gray-200 text-[12.5px] outline-none focus:border-[#46A8F9]" style={{ color: NAVY }} />
                    <button onClick={handleSaveAuth} disabled={!authNumber.trim() || savingAuth}
                      className={`py-2 px-4 rounded-lg text-[12px] font-semibold border-none flex items-center justify-center gap-1.5 ${authNumber.trim() && !savingAuth ? 'bg-[#46A8F9] text-white cursor-pointer' : 'bg-gray-200 text-gray-400'}`}>
                      {savingAuth && <OrellaSpinner size={16} tone="inherit" />}
                      {savingAuth ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* ━━ 4. UPLOAD RESULTS ━━ */}
        <SectionLabel text="Upload results" />
        <div className="mx-4 mb-4 rounded-[14px] border-[1.5px] border-gray-300 bg-white overflow-hidden" style={{ borderTopWidth: 3, borderTopColor: NAVY }}>
          <div className="p-3.5">
            {uploadedFiles.length > 0 && (
              <div className="mb-3 space-y-1.5">
                {uploadedFiles.map(f => (
                  <div key={f.id} className="flex items-center gap-2 py-1.5 px-2.5 rounded-lg border" style={{ background: GREEN + '08', borderColor: GREEN + '25' }}>
                    <Check size={12} style={{ color: GREEN_DARK }} />
                    <span className="text-[12px] font-medium text-gray-700 flex-1 truncate">{f.name}</span>
                    {f.url && <a href={f.url} target="_blank" rel="noopener noreferrer" className="text-[10px] font-semibold" style={{ color: BLUE }}>{t('common.view', 'View')}</a>}
                    {!f.existing && <button onClick={async () => {
                      const updated = uploadedFiles.filter(x => x.id !== f.id)
                      setUploadedFiles(updated)
                      // PROD-1042: the API no longer infers a removal from the shorter list.
                      await updateDeliverTask(taskKey, { removeFiles: deliverEntryIdentity(f) })
                    }} className="p-0.5"><X size={10} className="text-gray-400" /></button>}
                  </div>
                ))}
              </div>
            )}
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleFileSelect} className="hidden" />
            <input ref={fileInputRef} type="file" accept="image/*,.pdf" multiple onChange={handleFileSelect} className="hidden" />
            {/* Drag & drop zone */}
            <div ref={dropRef} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
              className={`rounded-[10px] border-2 border-dashed p-5 text-center transition-colors mb-2 ${isDragging ? 'border-[#46A8F9] bg-[#E9F8FF]' : 'border-gray-200 bg-[#FAFAFA]'}`}>
              <Upload size={22} className={isDragging ? 'text-[#46A8F9] mx-auto mb-1.5' : 'text-gray-300 mx-auto mb-1.5'} />
              <p className={`text-[12px] font-semibold ${isDragging ? 'text-[#46A8F9]' : 'text-gray-400'}`}>
                {isDragging ? t('furtherTesting.dropFilesHere') : t('furtherTesting.dragDropFilesHere')}
              </p>
              <p className="text-[10px] text-gray-400 mt-0.5">{t('furtherTesting.orUseButtonsBelow')}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => cameraInputRef.current?.click()} disabled={uploading}
                className="flex-1 py-3 rounded-[10px] border-[1.5px] border-gray-200 bg-white flex items-center justify-center gap-1.5 transition-colors hover:border-[#46A8F9] active:bg-gray-50">
                <Camera size={16} className="text-gray-400" /><span className="text-[11px] font-semibold text-gray-500">{t('furtherTesting.takePhoto')}</span>
              </button>
              <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                className="flex-1 py-3 rounded-[10px] border-[1.5px] border-gray-200 bg-white flex items-center justify-center gap-1.5 transition-colors hover:border-[#46A8F9] active:bg-gray-50">
                <Upload size={16} className="text-gray-400" /><span className="text-[11px] font-semibold text-gray-500">{t('furtherTesting.uploadFile')}</span>
              </button>
            </div>
            {uploading && <p className="text-[10px] text-[#46A8F9] text-center mt-2 animate-pulse">{t('furtherTesting.uploadingSaving')}</p>}
            <p className="text-[10px] text-gray-400 text-center mt-1.5">{t('furtherTesting.imagesOrPdf')}</p>
          </div>
        </div>
      </div>

      {/* ━━ FOOTER ━━ */}
      <div className="p-4 pb-6 bg-white border-t border-gray-100 flex-shrink-0">
        <div className="flex items-center gap-2.5">
          <button onClick={handleSave} disabled={isSubmitting}
            className="px-5 py-3.5 rounded-full border-[1.5px] border-gray-200 text-[13px] font-semibold text-gray-500 flex-shrink-0 active:bg-gray-50">
            {isSubmitting ? 'Saving...' : 'Save'}
          </button>
          <button onClick={handleMarkComplete} disabled={isSubmitting}
            className="flex-1 py-3.5 rounded-[14px] text-[14px] font-bold flex items-center justify-center gap-2 text-white active:scale-[0.98]"
            style={{ background: `linear-gradient(135deg, ${NAVY}, #1E4A6E)` }}>
            <Check size={16} /> {isSubmitting ? 'Saving...' : 'Mark as Completed'}
          </button>
        </div>
      </div>
    </div>
  )
}
